import { useTranslation } from 'react-i18next';
import { MailTempData, MailTempEnum } from '../../types';
import { Form, Input, Modal, Select, Spin, Switch, Typography } from 'antd';
import { useEffect, useState } from 'react';
import TextEditor from '../textEditor/TextEditor';
import { postDataWithAuthToken } from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';
import TextArea from 'antd/lib/input/TextArea';

type MailTemplateModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  callBack?: () => void;
  mailTempEnums?: MailTempEnum;
  selectedMailTemp?: MailTempData;
};

const MailTemplateModal = ({
  visible,
  setVisible,
  callBack,
  mailTempEnums,
  selectedMailTemp,
}: MailTemplateModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTempCode, setSelectedTempCode] = useState<{
    code: string;
    variables: string;
    subject: string;
    content: string;
  }>();
  const [isHtml, setIsHtml] = useState<boolean>(true);
  //Text Editor Components
  const [clear, setClear] = useState(false);
  const [HTMLText, setHTMLText] = useState<string>();
  const [fullscreenMode, setFullScreenMode] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setHTMLText(undefined);
      if (selectedMailTemp) {
        setIsHtml(selectedMailTemp.isHtml);
        setHTMLText(selectedMailTemp.templateContent);
        form.setFieldsValue({ templateCode: selectedMailTemp.templateCode });
        mailTempEnums &&
          mailTempEnums.tempCodeList.forEach((templateCode) => {
            if (templateCode.code === selectedMailTemp.templateCode) {
              setSelectedTempCode(templateCode);
              form.resetFields(['variables', 'templateSubject']);
            }
          });
      } else {
        setHTMLText(undefined); // Reset HTMLText when modal is closed
      }
    }
  }, [visible, form, selectedMailTemp, mailTempEnums]);

  useEffect(() => {
    if (clear) {
      form.resetFields(['templateContent']);
      setClear(false);
    }
  }, [clear, form]);

  const onConfirm = () => {
    form
      .validateFields()
      .then((values) => {
        console.log(values);
        setIsLoading(true);

        postDataWithAuthToken(
          selectedMailTemp ? 'mail_temp/edit' : 'mail_temp/add',
          {
            ...values,
            templateId: selectedMailTemp
              ? selectedMailTemp.templateId
              : undefined,
            templateContent: HTMLText ? HTMLText : '',
          }
        )
          .then((response) => {
            if (response && response.goodStatus) {
              setIsLoading(false);
              alertMessage(
                'success',
                selectedMailTemp
                  ? t('general.alerts.edited', {
                      name: t('plugins.Mail.mailTemp.title'),
                    })
                  : t('general.alerts.added', {
                      name: t('plugins.Mail.mailTemp.title'),
                    })
              );
              callBack && callBack();
              onClose();
            } else {
              setIsLoading(false);
              alertMessage(
                'error',
                response?.msg || t('general.noResponse'),
                response?.data || undefined
              );
            }
          })
          .catch((err) => {
            setIsLoading(false);
            console.log(err);
          });
      })
      .catch((err) => console.log(err));
  };

  const onClose = () => {
    setSelectedTempCode(undefined);
    setHTMLText(undefined);
    setIsHtml(true);
    setVisible(false);
  };

  return (
    <Modal
      width={fullscreenMode ? '100%' : 860}
      title={
        selectedMailTemp
          ? `${t(`plugins.Mail.mailTemp.editTitle`)} ${
              selectedMailTemp.templateCode
            }`
          : `${t(`plugins.Mail.mailTemp.addTitle`)}`
      }
      onOk={onConfirm}
      visible={visible}
      onCancel={onClose}
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={
            selectedMailTemp
              ? {
                  ...selectedMailTemp,
                  templateType: selectedMailTemp.templateType.code,
                }
              : { isHtml: isHtml }
          }
        >
          <Form.Item
            label={t('plugins.Mail.mailTemp.templateType')}
            name="templateType"
            rules={[
              {
                required: true,
                message: t('general.inputError.pleaseSelectOne'),
              },
            ]}
          >
            <Select
              placeholder={t('general.inputError.pleaseSelectOne')}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
            >
              {mailTempEnums?.tempTypeList.map((tempType) => (
                <Select.Option key={tempType.code} value={tempType.code}>
                  {tempType.description}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t('plugins.Mail.mailTemp.templateCode')}
            name="templateCode"
            rules={[
              {
                required: true,
                message: t('general.inputError.pleaseSelectOne'),
              },
            ]}
          >
            <Select
              placeholder={t('general.inputError.pleaseSelectOne')}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              filterOption={false}
              onChange={(value) => {
                mailTempEnums &&
                  mailTempEnums.tempCodeList.forEach((templateCode) => {
                    if (templateCode.code === value) {
                      form.resetFields([
                        'variables',
                        'templateSubject',
                        'templateContent',
                      ]);
                      form.setFieldsValue({
                        templateSubject: templateCode.subject,
                        templateContent: templateCode.content,
                      });
                      setHTMLText(templateCode.content);
                      setSelectedTempCode(templateCode);
                    }
                  });
              }}
            >
              {mailTempEnums?.tempCodeList.map((templateCode) => (
                <Select.Option
                  key={templateCode.code}
                  value={templateCode.code}
                >
                  {templateCode.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="isHtml"
            label={t('plugins.Mail.mailTemp.isHtml')}
            valuePropName="checked"
            rules={[
              {
                required: true,
                message: t('general.inputError.pleaseSelectOne'),
              },
            ]}
          >
            <Switch
              onChange={(value) => {
                setIsHtml(value);
              }}
            />
          </Form.Item>
          <Form.Item
            name={'templateSubject'}
            label={t('plugins.Mail.mailTemp.templateSubject')}
            rules={[
              {
                required: true,
                message: t('general.inputError.pleaseSelectOne'),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={'templateContent'}
            label={t('plugins.Mail.mailTemp.templateContent')}
            required
            rules={[
              isHtml
                ? {}
                : {
                    required: true,
                    message: t('general.inputError.pleaseSelectOne'),
                  },
            ]}
          >
            {isHtml ? (
              <TextEditor
                fullscreenMode={fullscreenMode}
                setFullscreenMode={setFullScreenMode}
                setClear={setClear}
                data={HTMLText}
                setData={setHTMLText}
              />
            ) : (
              <TextArea />
            )}
          </Form.Item>

          {selectedTempCode?.variables && (
            <Form.Item
              label={t('plugins.Mail.mailTemp.variables')}
              name="variables"
            >
              <Typography.Text>{selectedTempCode.variables}</Typography.Text>
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default MailTemplateModal;
